import React from 'react';
import { MapPin, MessageCircle, User, Award } from 'react-feather';
import {
  InlineShell,
  InlineEyebrow,
  CardButton,
  hashHue,
  type F8CardData,
  type OnAction,
} from './cardHelpers';
import { normalizeInterests } from '../../../utils/interests';

export default function UserProfileCard({
  card,
  onAction,
}: {
  card: F8CardData;
  onAction?: OnAction;
}) {
  const data = card?.data || {};
  const userId: string = data.userId;
  const tetherHandle: string = data.tetherHandle || data.handle || '';
  const handleDisplay = tetherHandle ? `@${tetherHandle}` : '';
  const firstName: string = data.firstName || data.name || '';
  const photo: string | null = data.avatar || data.photoURL || null;
  const bio: string = data.bio || data.aboutMe || '';
  const city: string = data.city || '';
  const country: string = data.country || '';
  const interests: string[] = normalizeInterests(data.interests);
  const badgeCount: number = data.badgeCount || 0;
  const isOnline: boolean = !!data.isOnline;

  const initial = (tetherHandle || firstName || '?')[0].toUpperCase();
  const hue = hashHue(tetherHandle || firstName);
  const location = [city, country].filter(Boolean).join(', ');

  const heroStyle: React.CSSProperties = {
    background: `linear-gradient(135deg,
      oklch(0.50 0.22 ${hue}) 0%,
      oklch(0.55 0.24 ${(hue + 30) % 360}) 50%,
      oklch(0.65 0.26 ${(hue + 60) % 360}) 100%)`,
  };

  return (
    <InlineShell>
      <InlineEyebrow label="profile" trailing={isOnline ? 'online' : undefined} />

      <div className="f8c-profile-head">
        <div className="f8c-profile-hero" style={heroStyle}>
          {photo ? (
            <img className="f8c-profile-hero-photo" src={photo} alt={handleDisplay || firstName} />
          ) : (
            <span className="f8c-profile-hero-initial">{initial}</span>
          )}
        </div>

        <div className="f8c-profile-identity">
          <div className="f8c-profile-handle">{handleDisplay || firstName || 'Unknown'}</div>
          {location && (
            <div className="f8c-profile-meta">
              <MapPin size={11} />
              <span>{location}</span>
            </div>
          )}
          {badgeCount > 0 && (
            <div className="f8c-profile-badges">
              <Award size={12} color="#f0abfc" />
              <span>
                {badgeCount} {badgeCount === 1 ? 'badge' : 'badges'}
              </span>
            </div>
          )}
        </div>
      </div>

      {bio && <p className="f8c-profile-bio">{bio}</p>}

      {interests.length > 0 && (
        <div className="f8c-profile-interests">
          {interests.map((tag) => (
            <span key={tag} className="f8c-profile-interest">
              {tag}
            </span>
          ))}
        </div>
      )}

      <div className="f8c-profile-actions">
        <CardButton
          primary
          onClick={() =>
            onAction?.({
              action: 'navigate',
              params: {
                screen: 'UserChatScreen',
                recipientId: userId,
                recipientName: handleDisplay || firstName,
              },
            })
          }
        >
          <MessageCircle size={13} />
          Message
        </CardButton>
        <CardButton
          onClick={() =>
            onAction?.({
              action: 'navigate',
              params: { screen: 'PublicUserProfile', userId },
            })
          }
        >
          <User size={13} />
          View profile
        </CardButton>
      </div>
    </InlineShell>
  );
}
